import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle,
  ChevronDown,
  Droplet,
  ExternalLink,
  Flame,
  Heart,
  Leaf,
  Shield,
  Sparkles,
  Sun,
  Zap,
} from "lucide-react";
import { api } from "@/lib/api";

const ICONS = { Leaf, Shield, Sun, Sparkles, Heart, Droplet, CheckCircle, Zap, Flame };
const ATTRIBUTES = [
  ["uses", "Uses"],
  ["suitable_weather", "Suitable weather"],
  ["good_for", "Good for"],
  ["aroma", "Aroma"],
  ["nutrients", "Nutrients"],
  ["flavour", "Flavour"],
];
const COMPARISON_COLUMN_SURFACES = ["bg-[#F8FBF8]", "bg-[#F1F6F2]", "bg-[#ECF3EE]"];

export const ProductExperienceLuxury = ({ product }) => {
  const [openBenefit, setOpenBenefit] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const story = product.product_story || [];
  const useCases = product.use_cases || [];
  const benefits = product.benefits || [];
  const faqs = product.faqs || [];
  const comparisonIds = useMemo(
    () => product.comparison_product_ids || [],
    [product.comparison_product_ids],
  );
  const comparisonKey = comparisonIds.join(",");

  useEffect(() => {
    if (!comparisonKey) {
      setCatalogue([]);
      return;
    }
    api.get("/products", { params: { limit: 100 } })
      .then((response) => setCatalogue(response.data.items || response.data || []))
      .catch(() => setCatalogue([]));
  }, [comparisonKey]);

  const comparedProducts = useMemo(() => {
    const byId = new Map(catalogue.map((item) => [item.id, item]));
    return [product, ...comparisonIds.map((id) => byId.get(id)).filter(Boolean)];
  }, [catalogue, comparisonIds, product]);

  return (
    <div className="mt-20" data-testid="pdp-product-experience">
      {story.length > 0 && (
        <section className="bg-white py-16" data-testid="pdp-product-story-section">
          <div className="mx-auto max-w-6xl px-4 text-center sm:px-8">
            <h2 className="font-serif text-3xl font-bold text-[#254734] sm:text-4xl">Product Story</h2>
          </div>
          <div className="mt-10 grid gap-8 md:grid-cols-2">
            {story.map((block, index) => {
                const dark = block.background_color === "#254734";
                const full = block.type === "full_width";
                return (
                  <article
                    key={block.id || index}
                    className={full ? "md:col-span-2" : ""}
                    style={{ backgroundColor: block.background_color || "#D9C4A0" }}
                  >
                    {full && (
                      <h3
                        className="mx-auto w-full max-w-6xl px-4 pt-8 text-center font-serif text-2xl font-bold sm:px-8 sm:text-3xl"
                      >
                        {block.heading}
                      </h3>
                    )}
                    <div className={full ? "flex flex-col" : "flex h-full flex-col"}>
                      {block.image && (
                        <img
                          alt={block.heading || product.name}
                          className={full ? "aspect-[16/7] w-full object-cover" : "aspect-[4/3] w-full object-cover"}
                          src={block.image}
                        />
                      )}
                      {full ? (
                        block.tagline && (
                          <p className="mx-auto max-w-3xl px-4 py-8 text-center text-base leading-7 sm:px-8">
                            {block.tagline}
                          </p>
                        )
                      ) : (block.heading || block.tagline) ? (
                        <div
                          className="flex min-h-64 flex-1 flex-col justify-end p-8 sm:p-10"
                          style={{ color: dark ? "#F8F5EE" : "#254734" }}
                        >
                          <h3 className="mt-5 font-serif text-3xl font-bold sm:text-4xl">
                            {block.heading}
                          </h3>
                          <p className="mt-5 max-w-md text-base leading-7">{block.tagline}</p>
                        </div>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
        </section>
      )}

      <section className="bg-[#E8F0EA] px-4 py-16 sm:px-8" data-testid="pdp-use-cases-section">
          <div className="mx-auto max-w-3xl text-center text-[#254734]">
            <h2 className="mt-4 font-serif text-3xl font-bold sm:text-4xl">{product.use_cases_heading || "Use Cases"}</h2>
            <ul className="mx-auto mt-12 max-w-xl space-y-6 text-left text-lg font-semibold leading-8">
              {useCases.map((item, index) => (
                <li key={`${item}-${index}`} className="flex gap-4">
                  <CheckCircle className="mt-1 shrink-0" size={21} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
      </section>

      {benefits.length > 0 && (
        <section className="bg-white px-4 py-16 sm:px-8" data-testid="pdp-benefits-section">
          <div className="mx-auto max-w-6xl text-center">
            <p className="text-xs font-bold uppercase text-[#254734]">Why it belongs in your routine</p>
            <h2 className="mt-4 font-serif text-3xl font-bold text-[#254734] sm:text-4xl">Benefits</h2>
            <div className="mt-12 grid gap-4 md:grid-cols-2">
              {benefits.map((benefit, index) => {
                const Icon = ICONS[benefit.icon] || Leaf;
                const open = openBenefit === index;
                return (
                  <div key={benefit.id || index} className="bg-[#E8F0EA] p-6">
                    <button
                      className="flex w-full items-center gap-4 text-left text-[#254734]"
                      data-testid={`pdp-benefit-toggle-${index}`}
                      onClick={() => setOpenBenefit(open ? null : index)}
                      type="button"
                    >
                      <Icon size={23} />
                      <span className="flex-1 font-serif text-2xl font-bold">{benefit.name}</span>
                      <ChevronDown className={open ? "rotate-180" : ""} size={20} />
                    </button>
                    {open && <p className="mt-5 pr-8 text-sm leading-7 text-[#254734]">{benefit.description}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {(product.shelf_life || Number(product.estimated_duration_days) > 0) && (
        <section className="bg-[#E8F0EA] px-4 py-14 sm:px-8" data-testid="pdp-shelf-life-section">
          <div className="mx-auto max-w-4xl text-center text-[#254734]">
            <p className="text-xs font-bold uppercase">Product care</p>
            <h2 className="mt-4 font-serif text-3xl font-bold sm:text-4xl">Shelf Life</h2>
            <p className="mt-5 text-base leading-7">
              {product.shelf_life || `Best enjoyed within ${product.estimated_duration_days} days from packing.`}
            </p>
          </div>
        </section>
      )}

      {comparisonIds.length > 0 && (
        <section className="bg-white px-4 py-16 sm:px-8" data-testid="pdp-comparison-section">
          <div className="mx-auto max-w-7xl text-[#254734]">
            <div className="text-center">
              <h2 className="font-serif text-2xl font-bold sm:text-3xl">Discover the Difference</h2>
            </div>
            <div className="mt-8 overflow-x-auto pb-4" data-testid="pdp-comparison-scroll">
              <table className="w-max min-w-full table-fixed text-left">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 w-28 bg-white" />
                    {comparedProducts.map((item, columnIndex) => (
                      <th
                        key={item.id}
                        className={`w-44 min-w-44 px-4 pb-5 align-top text-center ${
                          COMPARISON_COLUMN_SURFACES[columnIndex % COMPARISON_COLUMN_SURFACES.length]
                        }`}
                      >
                          <Link
                            className="relative block text-[#254734] transition-transform hover:-translate-y-0.5"
                            data-testid={`pdp-comparison-product-${item.id}`}
                            to={`/product/${item.slug || item.id}`}
                          >
                            <span
                              className="absolute -right-3 top-1 inline-flex h-6 w-6 items-center justify-center text-[#254734]"
                              data-testid={`pdp-comparison-link-icon-${item.id}`}
                            >
                              <ExternalLink size={14} aria-hidden="true" />
                            </span>
                            <div className="mx-auto h-28 w-28 overflow-hidden bg-white/70">
                              <img
                                alt={item.name}
                                className="h-full w-full object-cover object-center"
                                src={item.images?.[0]}
                              />
                            </div>
                            <span className="mt-3 block min-h-10 text-sm font-bold">
                              {item.name}
                            </span>
                          </Link>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ATTRIBUTES.map(([key, label]) => (
                    <tr key={key}>
                      <th className="sticky left-0 z-10 w-28 bg-[#F3F5F2] px-3 py-4 text-xs font-bold text-[#254734]">
                        {label}
                      </th>
                      {comparedProducts.map((item, columnIndex) => (
                        <td
                          key={item.id}
                          className={`w-44 min-w-44 px-4 py-4 text-sm leading-6 ${
                            COMPARISON_COLUMN_SURFACES[columnIndex % COMPARISON_COLUMN_SURFACES.length]
                          } ${
                            item.id === product.id ? "font-semibold text-[#254734]" : "text-[#254734]"
                          }`}
                        >
                          {product.comparison_attributes?.[key]?.[item.id] || "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {faqs.length > 0 && (
        <section className="bg-white px-4 py-16 sm:px-8" data-testid="pdp-faq-section">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-xs font-bold uppercase text-[#254734]">Details, clarified</p>
            <h2 className="mt-4 font-serif text-3xl font-bold text-[#254734] sm:text-4xl">FAQ</h2>
            <div className="mt-12 space-y-4 text-left">
              {faqs.map((faq, index) => {
                const open = openFaq === index;
                return (
                  <div key={faq.id || index} className="bg-[#E8F0EA] p-6 text-[#254734]">
                    <button
                      className="flex w-full items-center gap-6 text-left"
                      data-testid={`pdp-faq-toggle-${index}`}
                      onClick={() => setOpenFaq(open ? null : index)}
                      type="button"
                    >
                      <span className="flex-1 font-serif text-2xl font-bold">{faq.question}</span>
                      <ChevronDown className={open ? "rotate-180" : ""} size={21} />
                    </button>
                    {open && <p className="mt-5 max-w-3xl text-sm leading-7">{faq.answer}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};